import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {locationsApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {ContractStatusEnum} from 'src/enums';
import {useCan} from 'src/permissions/hooks';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {AutocompleteValue, BaseLocationInfo} from 'src/types';
import {handleScroll, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {store} from 'src/app/store';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ContractExportButton} from '../ContractExportButton';
import {ContractsTableActionsType} from './ContractsTableActions.types';

type LocationsQueryTrigger = ReturnType<typeof locationsApi.useLazyGetLocationsQuery>[0];

const ALL = '';
const PAGE_SIZE = 10;

export const handleGetLocations = async (
    trigger: LocationsQueryTrigger,
    businessId: number,
    companyId: number,
    page: number,
    pageSize: number,
    keyword?: string
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        return await trigger({
            data: {
                businessId,
                companyId,
                keyword,
            },
            params: {
                size: pageSize,
                page: page,
            },
        }).unwrap();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message ?? error.data?.error);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

/**
 * The contracts toolbar.
 *
 * Two filters, location and status. The old form declared a third, business, but the page is already
 * scoped to one business — chosen in the sidebar — and the list it was given was always empty, so it
 * is not carried over.
 *
 * The locations select loads a page at a time and keeps what it has seen: a business with hundreds of
 * sites would otherwise send its whole list down to fill one dropdown.
 */
export const ContractsTableActions: FC<ContractsTableActionsType> = ({
                                                                        handleSearch,
                                                                        handleFilter,
                                                                        businessId,
                                                                        filters,
                                                                        title,
                                                                        icon,
                                                                        onAdd,
                                                                        addLabel,
                                                                        totalElements,
                                                                    }) => {
    const intl = useIntl();
    const canExportContracts = useCan('contracts.export');
    const {authInfo} = useAppSelector((state) => state.common);
    const [getLocations, locations] = locationsApi.useLazyGetLocationsQuery();

    const [page, setPage] = useState(0);
    const [keyword, setKeyword] = useState<string>('');
    const [locationsData, setLocationsData] = useState<Array<BaseLocationInfo>>([]);

    useEffect(() => {
        if (businessId) {
            void handleGetLocations(
                getLocations,
                businessId,
                authInfo?.company?.id,
                page,
                PAGE_SIZE,
                keyword
            );
        }
    }, [businessId, page]);

    useEffect(() => {
        const content = locations?.data?.content ?? [];
        const merged = [...locationsData, ...content].reduce((acc, cur) => ({...acc, [cur.id]: cur}), {});
        setLocationsData(Array.from(Object.values(merged)));
    }, [locations?.data?.content]);

    // Each field read from the filter the list is on unless this call is the one changing it. The
    // table merges the result onto the rest, so a status set by the strip above is never clobbered.
    const apply = (next: {locationId?: number; status?: string}) => {
        handleFilter({
            businessId,
            locationId: 'locationId' in next ? next.locationId : filters.locationId,
            status: (('status' in next ? next.status : filters.status) || undefined) as
                ContractStatusEnum | undefined,
        });
    };

    const locationOptions = useMemo((): AutocompleteValue[] =>
        locationsData.map((item) => ({id: item.id, label: item.name})), [locationsData]);

    // Rendered from the filter the list is actually on, not from a copy of its own: the strip above
    // sets the status, and a select reading "all" over a narrowed list is worse than no select.
    const status = filters.status ?? ALL;
    // The chosen location's label comes from the pages of locations loaded so far, so a filter
    // restored from the address bar shows its name once the page holding it has arrived.
    const location = useMemo(
        () => locationOptions.find((item) => item.id === filters.locationId) ?? null,
        [locationOptions, filters.locationId]
    );

    const statusOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allStatuses'}), value: ALL},
        // REJECTED and VALIDATION are excluded, as the old form excluded them: no contract in the
        // data reaches either, and the enum carries them for a flow that was never built.
        ...Object.values(ContractStatusEnum)
            .filter((value) => value !== ContractStatusEnum.REJECTED
                && value !== ContractStatusEnum.VALIDATION)
            .map((value, index) => ({id: index + 1, name: value, value})),
    ], [intl]);

    const fields: FilterBarField[] = [
        {
            id: 'location',
            wide: true,
            control: (
                <AMPAutocomplete
                    input="locationId"
                    options={locationOptions}
                    value={location ?? undefined}
                    placeholder={intl.formatMessage({id: 'location'})}
                    onChange={(_event: SyntheticEvent, value) => {
                        apply({locationId: (value as AutocompleteValue | null)?.id});
                    }}
                    onInputChange={(_event, value) => {
                        setPage(0);
                        setKeyword(value);
                    }}
                    handleScroll={() => handleScroll(
                        locations?.data?.page?.number ?? 0,
                        locations?.data?.page?.totalPages ?? 0,
                        page,
                        setPage
                    )}
                />
            ),
        },
        {
            id: 'status',
            control: (
                <AMPFormSelect
                    dense
                    data={statusOptions}
                    value={status}
                    input="status"
                    onChange={(e) => apply({status: String(e.target.value)})}
                />
            ),
        },
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            fields={fields}
            hasActiveFilters={Boolean(location || status)}
            onClearAll={() => apply({locationId: undefined, status: ALL})}
            actions={(
                <>
                    <OutlinedInput
                        sx={searchInputSx}
                        color="secondary"
                        type="text"
                        placeholder={intl.formatMessage({id: 'search'})}
                        name="search"
                        // Uncontrolled for the debounce, but seeded: the box has to say what the list is
                        // filtered by after a Back, which remounts it with the keyword still in the URL.
                        defaultValue={filters?.keyword ?? ''}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                            debounce(() => handleSearch(event.target.value), 1000)()}
                        startAdornment={<Search/>}
                    />
                    {canExportContracts && <ContractExportButton filters={filters}/>}
                </>
            )}
        />
    );
};
